/*
  site-header.js
  WHAT: BlueAnt site header behaviour — Ask Anton trigger, cart count live update, mobile toggle event.
  WHY:  Minimal JS surface. No frameworks. Hot-reload friendly.
  WHEN: Added 2026-05-28 with site-header build.
*/

(function () {
  'use strict';

  // ─── Cart drawer (mini tray) construction ───────────────────────────────
  // [ref: ba-cart-drawer-init]
  // Impulse builds its cart drawer in theme.js `initDrawers()`, which only runs from
  // `theme.HeaderSection` — registered against `data-section-type="header"`. This build
  // replaced the stock header with `site-header.liquid`, whose section type is
  // "site-header", so initDrawers has NEVER run: `theme.CartDrawer` was never
  // constructed, nothing listened for `ajaxProduct:added` / `cart:open`, and every add
  // to cart therefore navigated to /cart instead of opening the tray. Verified on the
  // local dev server: dispatching `cart:open` on document did nothing before this.
  //
  // Constructed here rather than by registering HeaderSection, because HeaderSection
  // also drives Impulse's own sticky nav, mega menu and disclosures against markup this
  // theme does not have.
  //
  // Guards are copied verbatim from initDrawers so behaviour matches stock Impulse:
  // drawer cart type only, and never on the cart page itself (which has its own form).
  if (!window.__baCartDrawer &&
      window.theme && theme.CartDrawer &&
      theme.settings && theme.settings.cartType === 'drawer' &&
      !document.body.classList.contains('template-cart') &&
      document.getElementById('CartDrawerForm')) {
    // Cached so a section re-render in the theme editor cannot bind a second drawer,
    // which would double every cart event.
    window.__baCartDrawer = new theme.CartDrawer();
  }

  const header = document.querySelector('.ba-site-header');
  if (!header) return;

  // ─── Ask Anton — opens Intercom messenger ───────────────────────────────
  // Owner 2026-06-18: "Need help?" nav button should open Anton chat (Intercom
  // messenger with Fin AI greeting), not jump straight to a new-message form.
  //
  //  - Button with NO `data-anton-prompt` → Intercom('show') opens the
  //    messenger home so Anton/Fin can greet the user with quick-replies and
  //    suggested topics. This is the standard "open chat" entry point.
  //  - Button WITH `data-anton-prompt` → Intercom('showNewMessage', prompt)
  //    skips the greeter and prefills the composer with the prompt (used by
  //    PDP concierge chips like "Compare with similar" / "What's the warranty?").
  //  - If Intercom isn't loaded yet (network blocked, adblocker, missing
  //    workspace metafield), the calls are still safe — the boot snippet
  //    queues them and they fire once Intercom loads. We also dispatch
  //    a custom event for any subscriber and log to console for QA.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action="open-anton"]');
    if (!trigger) return;
    const prompt = trigger.getAttribute('data-anton-prompt') || '';

    // Owner 2026-10-06: "the still cant find what you need and ask anton on accessories is
    // broken."
    //
    // It was. This handler used to call preventDefault() unconditionally, at the top. When
    // Intercom is absent - adblocker, slow load, or simply not yet booted - the remaining code
    // dispatches a custom event nobody listens for and writes a console.info. So the click was
    // swallowed and NOTHING visible happened. On the accessories page that is the entire
    // failure: the band rendered, the button looked live, and pressing it did nothing.
    //
    // The proven pattern was already on the same page - the "Chat with Anton" link in the
    // picker is an <a href="/pages/support"> whose inline onclick returns true when Intercom is
    // missing, so it simply navigates. This now does the same thing for every Anton trigger at
    // once: suppress the default ONLY when Intercom can actually handle it, otherwise let the
    // anchor do its job.
    //
    // A <button> trigger with no Intercom still has no fallback - there is nowhere for it to
    // go - which is why support-anton-band.liquid now renders an anchor.
    if (typeof window.Intercom === 'function') {
      event.preventDefault();
    }

    if (typeof window.Intercom === 'function') {
      try {
        if (prompt) {
          window.Intercom('showNewMessage', prompt);
        } else {
          window.Intercom('show');
        }
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('[blueant] Intercom call failed', err);
        }
      }
      return;
    }
    document.dispatchEvent(new CustomEvent('blueant:anton:open', {
      bubbles: true,
      detail: { source: trigger.dataset.antonSource || 'site', prompt: prompt }
    }));
    if (window.console && console.info) {
      console.info('[blueant] Anton trigger clicked — Intercom not yet wired.', prompt);
    }
  });

  // ─── Mobile toggle ──────────────────────────────────────────────────────
  // Dispatches 'blueant:mobile-nav:open' for the future mobile drawer feature
  // to subscribe to. Manages aria-expanded on the button itself.
  header.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action="open-mobile-nav"]');
    if (!trigger) return;
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    document.dispatchEvent(new CustomEvent('blueant:mobile-nav:open', {
      bubbles: true,
      detail: { source: 'site-header', expanded: !expanded }
    }));
  });

  // ─── Cart count live update ─────────────────────────────────────────────
  // Subscribes to cart:updated events that other parts of the theme dispatch
  // after add/remove. Updates the badge in place + toggles is-empty.
  const cartCountEl = header.querySelector('[data-cart-count]');
  const cartLinkEl = header.querySelector('[data-action="open-cart"]');

  function updateCartCount(count) {
    if (!cartCountEl) return;
    const n = Number(count) || 0;
    cartCountEl.textContent = String(n);
    cartCountEl.classList.toggle('is-empty', n === 0);
    if (cartLinkEl) {
      const noun = n === 1 ? 'item' : 'items';
      cartLinkEl.setAttribute('aria-label', `Cart, ${n} ${noun}`);
    }
  }

  // Standard Shopify event used by Cart drawer / Cart AJAX patterns.
  document.addEventListener('cart:updated', (event) => {
    const cart = event && event.detail && event.detail.cart;
    if (cart && typeof cart.item_count !== 'undefined') {
      updateCartCount(cart.item_count);
    }
  });

  // Some apps fire on the window object instead.
  window.addEventListener('cart:updated', (event) => {
    const cart = event && event.detail && event.detail.cart;
    if (cart && typeof cart.item_count !== 'undefined') {
      updateCartCount(cart.item_count);
    }
  });

  // ─── Slot overlay dev-mode hook (visualises slot IDs in URL ?slots=1) ───
  // See DOCS/SLOT-FRAMEWORK.md §3.3. No-op when the param is absent.
  if (new URLSearchParams(location.search).has('slots')) {
    document.body.classList.add('blueant-slot-overlay');
    if (window.console && console.info) {
      console.info('[blueant] Slot overlay mode active — every [data-slot] is annotated.');
    }
  }
})();
